import React from 'react';
import {Badge, Modal, ModalBody, ModalHeader, Spinner} from 'reactstrap';
import {Mail} from 'lucide-react';
import {
    cappedListNote,
    useWorkspaceContacts,
    WorkspaceContactGroup
} from '../../../api/resource-allocation/useWorkspaceContacts';

interface WorkspaceContactsModalProps {
    isOpen: boolean;
    toggle: () => void;
}

const ContactGroup: React.FC<{group: WorkspaceContactGroup}> = ({group}) => {
    const note = cappedListNote(group);

    return (
        <section className="mb-4">
            <h6 className="mb-0">{group.label}</h6>
            <p className="text-muted small mb-2">{group.description}</p>
            {group.total === 0 ? (
                <p className="small fst-italic mb-0">
                    Nobody holds this role yet.
                </p>
            ) : (
                <ul className="list-unstyled mb-0">
                    {group.people.map(person => (
                        <li
                            key={person.personId}
                            className="d-flex flex-wrap align-items-center gap-2 py-1"
                        >
                            <span className="fw-medium">{person.name}</span>
                            {person.jobTitle && (
                                <span className="text-muted small">
                                    {person.jobTitle}
                                </span>
                            )}
                            {person.email && (
                                <a
                                    href={`mailto:${person.email}`}
                                    className="small d-inline-flex align-items-center gap-1"
                                >
                                    <Mail size={12} />
                                    {person.email}
                                </a>
                            )}
                            {person.sharesYourTeam && (
                                <Badge color="light" className="text-dark">
                                    Your team
                                </Badge>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {note && <p className="text-muted small mt-1 mb-0">{note}</p>}
        </section>
    );
};

/**
 * "Who manages this workspace?": the support contact, administrators,
 * maintainers and other contacts, so anyone can find who to ask for access,
 * a role or help. Opened from the user menu and the Customer Roles section.
 */
export const WorkspaceContactsModal: React.FC<WorkspaceContactsModalProps> = ({
    isOpen,
    toggle
}) => {
    const {data, isLoading, error} = useWorkspaceContacts(isOpen);

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
            <ModalHeader toggle={toggle}>
                Contacts - who manages this workspace?
            </ModalHeader>
            <ModalBody>
                <p className="text-muted small">
                    Ask the support contact first. Administrators grant customer
                    roles such as Maintainer; project access is granted by a
                    project's editors.
                </p>
                {isLoading && <Spinner size="sm" />}
                {error && (
                    <p className="text-danger small">
                        The contacts could not be loaded: {error.message}
                    </p>
                )}
                {data?.map(group => (
                    <ContactGroup key={group.role} group={group} />
                ))}
            </ModalBody>
        </Modal>
    );
};

export default WorkspaceContactsModal;
